import type {BasicPrimitiveType, Identified, Ordered, UUID} from 'src/typings';
import {assertTrue, isFunction, isUndefined} from './custom-utils';
import {stringifyArgument} from 'shared/utils/error-utils';
import {assertIsFunction} from 'shared/utils/types-utils';

export function ascendingOrderComparator<T extends Ordered>(item1: T, item2: T): number {
    return item1.order - item2.order;
}

export function descendingOrderComparator<T extends Ordered>(item1: T, item2: T): number {
    return item2.order - item1.order;
}

export function uuidComparator<T extends Identified<UUID>>(item1: T, item2: T): number {
    return item1.id.localeCompare(item2.id);
}

export function compositeComparator<T>(...comparators: ((a: T, b: T) => number)[]): (a: T, b: T) => number {
    return (a, b) => {
        for (const comparator of comparators) {
            const result = comparator(a, b);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    }
}

export type Predicate<T> = (item: T, index?: number) => boolean;

export function not<T>(predicate: Predicate<T>): Predicate<T> {
    return (item, index) => !predicate(item, index);
}

export function byIndex<T>(index: number): Predicate<T> {
    return (_, itemIndex) => itemIndex === index;
}

export function byReference<T>(value: T): Predicate<T> {
    return (item) => item === value;
}

export function byProperty<T, K extends keyof T>(property: K, value: T[K]): Predicate<T> {
    return (item) => item[property] === value;
}

export function byId<T extends Identified<ID>, ID>(idValue: ID): Predicate<T> {
    return byProperty('id', idValue);
}

export function byIdOf<T extends Identified<ID>, ID>(idHolder: Identified<ID>): Predicate<T> {
    return byId(idHolder.id);
}

/**
 * @description
 * Finds the first element in an array that matches the given predicate.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function findFirst<T>(array: T[], predicate: Predicate<T>): T | undefined {
    assertIsArray(array);
    assertIsFunction(predicate);

    return array.find(predicate);
}

/**
 * @description
 * Finds the first element in an array that matches the given predicate.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function findFirstIndex<T>(array: T[], predicate: Predicate<T>): number {
    assertIsArray(array);
    assertIsFunction(predicate);

    return array.findIndex(predicate);
}

/**
 * @description
 * Finds the first element in an array that matches the given predicate, or returns a default value if not found.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function findFirstOrGetDefault<T>(array: T[], predicate: Predicate<T>, defaultValue: T | (() => T)): T {
    const value = findFirst(array, predicate);
    if (isUndefined(value)) {
        return isFunction(defaultValue) ? defaultValue() : defaultValue;
    } else {
        return value;
    }
}

/**
 * @description
 * Checks if an array contains at least one element that matches the given predicate.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function anyMatches<T>(array: T[], predicate: Predicate<T>): boolean {
    assertIsArray(array);
    assertIsFunction(predicate);

    return array.some(predicate);
}

/**
 * @description
 * Returns a new array where all elements that match the predicate are replaced with the given replacement.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function replaceAll<T>(array: T[], predicate: Predicate<T>, replacement: T | (() => T)): T[] {
    assertIsArray(array);
    assertIsFunction(predicate);

    return array.map((originalValue, index) => {
        if (predicate(originalValue, index)) {
            return isFunction(replacement) ? replacement() : replacement;
        } else {
            return originalValue;
        }
    });
}

/**
 * @description
 * Replaces all elements in the original array that match the predicate with the given replacement.
 * This function mutates the original array.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function replaceAllInPlace<T>(array: T[], predicate: Predicate<T>, replacement: T | (() => T)): T[] {
    assertIsArray(array);
    assertIsFunction(predicate);

    for (let i = 0; i < array.length; i++) {
        if (predicate(array[i], i)) {
            array[i] = isFunction(replacement) ? replacement() : replacement;
        }
    }
    return array;
}

/**
 * @description
 * Returns a new array with all elements that do not match the predicate.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function removeAll<T>(array: T[], predicate: Predicate<T>): T[] {
    assertIsArray(array);
    assertIsFunction(predicate);

    return array.filter(not(predicate));
}

/**
 * @description
 * Removes all elements from the original array that match the predicate.
 * This function mutates the original array.
 *
 * @throws {TypeError} If `array` is not an array or `predicate` is not a function.
 */
export function removeAllInPlace<T>(array: T[], predicate: Predicate<T>): T[] {
    assertIsArray(array);
    assertIsFunction(predicate);

    let deletedCount = 0; // to recreate original indexes before removing previous elements
    for (let i = 0; i < array.length; i++) {
        if (predicate(array[i], i + deletedCount)) {
            array.splice(i, 1);
            deletedCount++;
            i--;
        }
    }
    return array;
}

/**
 * @description
 * Joins an array of primitive values into a string with a specified delimiter, prefix, and suffix.
 *
 * @throws {TypeError} If `array` is not an array.
 */
export function join<T extends BasicPrimitiveType>(array: T[], delimiter: string = ',', prefix: string = '', suffix: string = ''): string {
    assertIsArray(array);

    return array.reduce(((result, element) => {
        if (result.length > 0) {
            return `${result}${delimiter}${prefix}${element.toString()}${suffix}`;
        } else {
            return `${prefix}${element.toString()}${suffix}`;
        }
    }), '');
}

/**
 * @module array-utils.fp
 * @name range
 *
 * @param {number} start first number in sequence
 * @param {number} stop last number in sequence
 * @param {number} [step=1] difference between numbers in sequence
 * @returns {number[]}
 *
 * @description
 * Generates a sequence of numbers as array, defined by (inclusive) number range [start, ..., stop]
 */
export function range(start: number, stop: number, step: number = 1): number[] {
    if (step > 0 && stop < start) {
        throw new Error(`For step of ${step} start value should be less or equal to stop value`);
    }
    if (step < 0 && stop > start) {
        throw new Error(`For step of ${step} start value should be more or equal to stop value`);
    }
    return Array.from({length: (stop - start) / step + 1}, (_, i) => start + (i * step));
}

/**
 * @module array-utils.fp
 * @name sequence
 *
 * @description
 * Generates a sequence of numbers as array, defined by start number and length of the sequence
 */
export function sequence(start: number, length: number, step = 1): number[] {
    if (length < 0) {
        throw new Error(`Length ${length} of the sequence has to be positive integer or zero`);
    }
    return Array.from({length}, (_, i) => start + (i * step));
}


/**
 * @module array-utils.fp
 * @name includesAllFrom
 *
 * @template T
 * @param {T[]} testedArray array which we test if it contains all values from includedValues
 * @param {T[]} includedValues values that should be included inside testedArray
 * @returns {boolean}
 *
 * @description
 * Test whether all includedValues are present in testedArray
 */
export function includesAllFrom<T>(testedArray: T[], includedValues: T[]): boolean {
    assertIsArray(testedArray);
    assertIsArray(includedValues);

    return includedValues.every((element) => testedArray.includes(element));
}


/**
 * @module array-utils.fp
 * @name zip
 *
 * @template U
 * @template V
 * @param {U[]} array1 first input array
 * @param {V[]} array2 second input array of SAME LENGTH as the first array
 * @returns {[U, V][]}  array of tuples created from elements from both input arrays
 *
 * @description
 * Creates new array by combining elements with same index from input arrays into tuples.
 * Input arrays need to have SAME LENGTH.
 */
export function zip<U, V>(array1: U[], array2:V[]): [U, V][] {
    assertSameLength(array1, array2);

    return array1.map((value1,index) => [value1, array2[index]]);
}

export function isArray(arg: unknown): arg is any[] {
    return Array.isArray(arg);
}

export function assertIsArray(arg: unknown): asserts arg is any[] {
    if (!isArray(arg)) {
        throw new Error(`Argument ${stringifyArgument(arg)} is not an array`);
    }
}

export function isEmpty<T>(array: T[]) {
    assertIsArray(array);
    return array.length === 0;
}

export type NonEmptyArray<T> = [T, ...T[]];

export function isNotEmpty<T>(array: T[]): array is NonEmptyArray<T> {
    return !isEmpty(array);
}

export function assertIsNotEmpty<T>(array: T[]): asserts array is NonEmptyArray<T> {
    if (!isNotEmpty(array)) {
        throw new Error(`Argument ${stringifyArgument(array)} is empty`);
    }
}

export function asNotEmpty<T>(array: T[]): NonEmptyArray<T> {
    assertIsNotEmpty(array);
    return array;
}

export function assertHasAtLeastElements<T>(array: T[],
                                            length: number,
                                            msg = () => `Argument ${stringifyArgument(array)} has only ${array.length} elements, but at least ${length} is required`): void {
    assertIsArray(array);
    if (array.length < length) {
        throw new Error(msg());
    }
}

export function arrayShallowEqual<T>(array1: T[], array2: T[]) {
    assertIsArray(array1)
    assertIsArray(array2)

    if (array1 === array2) {
        return true;
    }
    if (array1.length !== array2.length) {
        return false;
    }
    for (let i = 0; i < array1.length; i++) {
        if (array1[i] !== array2[i]) {
            return false;
        }
    }
    return true;
}

export function assertSameLength<U, V>(array1: U[], array2: V[]) {
    assertIsArray(array1)
    assertIsArray(array2)

    assertTrue(array1.length === array2.length,
        `Arrays ${stringifyArgument(array1)}(${array1.length}) and ${stringifyArgument(array2)}(${array2.length}) are not same length`
    )
}

export function distinct<T>(array: T[]): T[] {
    assertIsArray(array);
    return Array.from(new Set(array));
}

export function distinctBy<VALUE, UNIQUE_KEY>(array: VALUE[], uniqueKeyGenerator: (_: VALUE) => UNIQUE_KEY): VALUE[] {
    assertIsArray(array);
    assertIsFunction(uniqueKeyGenerator);
    const uniqueIdToValueMap = new Map<UNIQUE_KEY, VALUE>(array.map((item) => [uniqueKeyGenerator(item), item]));
    return Array.from(uniqueIdToValueMap.values());
}

export function intersectionBy<VALUE, UNIQUE_KEY>(array1: VALUE[], array2: VALUE[], uniqueKeyGenerator: (_: VALUE) => UNIQUE_KEY): VALUE[] {
    assertIsArray(array1);
    assertIsArray(array2);
    assertIsFunction(uniqueKeyGenerator);
    const uniqueIdToValueMap1 = new Map<UNIQUE_KEY, VALUE>(array1.map((item) => [uniqueKeyGenerator(item), item]));
    const uniqueIdToValueMap2 = new Map<UNIQUE_KEY, VALUE>(array2.map((item) => [uniqueKeyGenerator(item), item]));
    const intersectionKeys = new Set(uniqueIdToValueMap1.keys()).intersection(new Set(uniqueIdToValueMap2.keys()));
    return Array.from(uniqueIdToValueMap1.entries().filter(([key,]) => intersectionKeys.has(key)).map(([,value]) => value));
}

export function matrixMap<SOURCE, TARGET>(matrix: SOURCE[][], mapFn: (_: SOURCE, index?: number) => TARGET): TARGET[][] {
    return matrix.map((row) => row.map(mapFn));
}
